import { Col, Row, Spin, Table, Typography } from "antd";
import { StyleSheet } from "../../StyleSheet";
import { useCallback, useEffect, useState } from "react";
import { NotificationRequest, NotificationResponse, NotificationType, Status } from "../../types/types";
import { ColumnsType } from "antd/es/table";
import { CheckCircleFilled, DeleteOutlined, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { deleteNotifications, getNotifications, setReadNotifications } from "../../methods/axiosMethods";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import { subMenusOfNotifications } from "../../commons/subMenus";
import CardDashboard from "../../components/CardDashboard";
import { image } from "../../commons/ImageRoutes";
import Buttons from "../../components/Buttons";
import dayjs from "dayjs";
import GenericModal from "../../components/GenericModal";
import ModalNotification, { AlertTypeNew } from "../../components/ModalNotification";
import CustomPagination from "../../components/CustomPagination";
import Search from "antd/es/input/Search";
import _ from "lodash";

const { Text } = Typography;
const NotificationsHome = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [showNotification, setShowNotification] = useState<boolean>(false);
    const [showModalInfo, setShowModalInfo] = useState<boolean>(false);
    const [isRead, setIsRead] = useState<boolean>(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState<AlertTypeNew>("SUCCESS");
    const [notificationList, setNotificationList] = useState<Array<NotificationResponse>>();
    const [notificationData, setNotificationData] = useState<NotificationResponse>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [totalPerPage, setTotalPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchWord, setSearchWord] = useState("");
    const hasSelected = selectedRowKeys.length > 0;
    const colorRead = "#52d0a5";
    const colorUnread = "#fe587f";

    const columns: ColumnsType<NotificationResponse> = [
        {
            title: "Acciones",
            dataIndex: "operation",
            key: "operation",
            width: "15%",
            align: "center",
            className: 'custom-header',
            render: (_: any, record: NotificationResponse) => (
                <Buttons
                    type={"primary"}
                    title={"Más Detalles"}
                    action={() => handleOpenDetails(record)}
                    color="#444cf9"
                    icon={<InfoCircleOutlined />}
                />
            ),
        },
        {
            title: "Estado",
            dataIndex: "read",
            key: "read",
            className: 'custom-header',
            render: (read: boolean) => (
                <div style={styles.infoText}>
                    <>
                        {read ? (
                            <CheckCircleFilled
                                style={
                                    {
                                        margin: 5,
                                        color: colorRead,
                                    }}
                            />
                        ) : (
                            <InfoCircleOutlined
                                style={
                                    {
                                        margin: 5,
                                        color: colorUnread,
                                    }}
                            />
                        )
                        }
                        <label
                            style={{
                                color: (read) ? colorRead : colorUnread,
                            }}
                        >
                            {read ? "Leida" : "No Leida"}
                        </label>
                    </>
                </div>
            ),
        },
        {
            title: "Tipo de notificación",
            dataIndex: "notificationType",
            key: "notificationType",
            className: 'custom-header',
            render: (_: any, record: NotificationResponse) => (
                record.notificationType === NotificationType.SCHEDULED_DISPERSION ? "Transferencia Programada" : null
            ),
        },
        {
            title: "Título",
            dataIndex: "title",
            key: "title",
            className: 'custom-header',
        },
        {
            title: "Descripción",
            dataIndex: "message",
            key: "message",
            className: 'custom-header',
        },
        {
            title: "Fecha y Hora",
            dataIndex: "createdAt",
            key: "createdAt",
            className: 'custom-header',
            render: (_: any, record: NotificationResponse) => (
                dayjs(record.createdAt).format("DD/MM/YYYY HH:mm")
            ),
        },
    ];

    const handleOpenDetails = (record: NotificationResponse) => {
        setShowModalInfo(true);
        setNotificationData(record);
        const request = {
            notificationIds: [record.notificationId]
        } as NotificationRequest;
        setReadNotifications(request)
            .then((response) => {
                if (response.status === Status.UPDATED) {
                    setIsRead(true);
                }
            })
            .catch(() => {
                setNotificationMessage("Ocurrió un error actualizar las notificaciones");
                setNotificationType('ERROR')
                setShowNotification(true);
            })
            .finally(() => {
                fetchData(currentPage, totalPerPage, searchWord);
            });
    }

    const ftnDeleteNotification = () => {
        const request = {
            notificationIds: selectedRowKeys
        } as NotificationRequest;
        setLoading(true);

        deleteNotifications(request)
            .then((response) => {
                if (response.status === Status.UPDATED) {
                    setNotificationType('SUCCESS');
                } else {
                    setNotificationType('ERROR');
                }
                setNotificationMessage(response.message);
            })
            .catch(() => {
                setNotificationMessage("Ocurrió un error eliminar las notificaciones");
                setNotificationType('ERROR');
            })
            .finally(() => {
                setLoading(false);
                setShowNotification(true);
                fetchData(currentPage, totalPerPage, searchWord);
            });
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [totalPerPage]);

    const fetchData = useCallback(
        (page?: number, pageChanged?: number, searchTerm?: string) => {

            setLoading(true);

            const currentPageTmp = page ? page - 1 : 0;
            const currentPageChangedTmp = pageChanged ?? 10;
            const searchTermTmp = searchTerm ? searchTerm : "";

            getNotifications(currentPageTmp, currentPageChangedTmp, 'title,asc', searchTermTmp)
                .then((response) => {
                    const { data } = response;
                    if (data?.status === Status.OK && data?.data?.content) {
                        const notificationsList = data.data.content;
                        setNotificationList(notificationsList);
                        setTotalRecords(data.data.totalElements);
                    } else {
                        setNotificationList([]);
                        setTotalRecords(0);
                    }
                })
                .catch(() => {
                    setNotificationList([]);
                    setTotalRecords(0);
                    setNotificationType("ERROR");
                    setNotificationMessage(
                        "Ocurrió un error al momento de obtener las notificaciones, intente más tarde."
                    );
                    setShowNotification(true);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        []
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page, totalPerPage, searchWord);
    };

    const handleTotalPerPage = (total: number) => {
        setTotalPerPage(total);
        fetchData(1, total, searchWord);
    };

    const handleSearch = async (text: string) => {
        setSearchWord(text);
        fetchData(1, totalPerPage, text);
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    return (
        <SubDefaultLayout childrenSubMenu={subMenusOfNotifications} title="Notificaciones">
            <Row gutter={[15, 25]}>
                <Col span={24}>
                    <Row gutter={[10, 10]}>
                        <Col xs={24} md={6} xl={4}>
                            <CardDashboard
                                title={"Programar Transferencia"}
                                icon={image.transferSchIcon}
                                action={`/transfers/scheduled/new`}
                                height={130}
                            />
                        </Col>
                        <Col xs={24} md={6} xl={4}>
                            <CardDashboard
                                title={"Historial de Transferencias"}
                                icon={image.historyIcon}
                                action={`/transfers/history`}
                                height={130}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col span={24}>
                    <div style={styles.primaryContainer}>
                        <Row gutter={[16, 16]} style={styles.rowContainer}>
                            <Col xs={24} md={8} xxl={5}>
                                <Search
                                    onSearch={(value: string) => handleSearch(value)}
                                    onChange={(value) =>
                                        _.isEmpty(value.target.value)
                                            ? (
                                                setSearchWord(""),
                                                fetchData(1, totalPerPage),
                                                setCurrentPage(1)
                                            )
                                            : null
                                    }
                                    placeholder="Buscar por Título o Descripción"
                                />
                            </Col>
                            <Col xs={0} md={5} xxl={12} />
                            <Col xs={24} md={11} xxl={7} style={{ display: 'flex', justifyContent: 'end' }} >
                                <Buttons
                                    type="primary"
                                    title="Borrar"
                                    color="#765CE9"
                                    isDisabled={!hasSelected}
                                    action={ftnDeleteNotification}
                                    icon={<DeleteOutlined />}
                                    iconLeft
                                />
                            </Col>
                        </Row>
                        <Col xs={24} xl={24} style={{ marginTop: 15 }}>
                            <Table
                                rowSelection={rowSelection}
                                rowKey={(record) => record.notificationId}
                                dataSource={notificationList}
                                columns={columns}
                                scroll={{ y: 400, x: 1300 }}
                                locale={{ emptyText: "No hay datos disponibles" }}
                                bordered
                                pagination={false}
                                loading={{
                                    spinning: loading,
                                    indicator: <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />,
                                }}
                            />
                        </Col>
                        <Col xs={24}>
                            <CustomPagination
                                currentPage={currentPage}
                                pageSize={totalPerPage}
                                paginationTotal={totalRecords}
                                onPageSizeChange={handleTotalPerPage}
                                onPageChange={handlePageChange}
                                showSizeChanger
                            />
                        </Col>
                    </div>
                </Col>
            </Row>
            <GenericModal
                title={"Más Detalles"}
                show={showModalInfo}
                onClose={() => setShowModalInfo(false)}
            >
                <div style={styles.divColumn} >
                    <Text style={styles.notiTitle}>Fecha y Hora</Text>
                    <Text style={styles.notiDesc}>{dayjs(notificationData?.createdAt).format("DD/MM/YYYY HH:mm")}</Text>
                    <Text style={styles.notiTitle}>Tipo de Notificación</Text>
                    <Text style={styles.notiDesc}>{notificationData?.notificationType === NotificationType.SCHEDULED_DISPERSION ?
                        "Transferencia Programada"
                        : null}
                    </Text>
                    <Text style={styles.notiTitle}>Titulo</Text>
                    <Text style={styles.notiDesc}>{notificationData?.title}</Text>
                    <Text style={styles.notiTitle}>Mensaje</Text>
                    <Text>{notificationData?.message}</Text>
                    <div style={{ ...styles.infoText, marginTop: 25 }}>
                        {isRead ? (
                            <CheckCircleFilled
                                style={
                                    {
                                        margin: 5,
                                        color: colorRead,
                                    }}
                            />
                        ) : (
                            <InfoCircleOutlined
                                style={
                                    {
                                        margin: 5,
                                        color: colorUnread,
                                    }}
                            />
                        )
                        }
                        <label
                            style={{
                                color: (isRead) ? colorRead : colorUnread,
                            }}
                        >
                            {isRead ? "Leida" : "No Leida"}
                        </label>
                    </div>
                </div>
            </GenericModal>
            <ModalNotification
                title={"Notificaciones"}
                message={notificationMessage}
                alertType={notificationType}
                show={showNotification}
                onClose={() => setShowNotification(false)}
            />
        </SubDefaultLayout >
    );
};

const styles = StyleSheet.create({
    primaryContainer: {
        width: "100%",
        margin: "20px auto",
        padding: "20px",
        borderRadius: 10,
        backgroundColor: 'white',
        boxShadow: "rgba(177, 179, 185, 0.51) 5px 8px 24px 5px",
    },
    rowContainer: {
        marginBottom: '20px',
    },
    notiTitle: {
        fontWeight: 'bold',
    },
    notiDesc: {
        marginBottom: 10
    },
    infoText: {
        display: "flex",
        alignItems: "center",
    },
    divColumn: {
        padding: 10,
        display: 'flex',
        flexDirection: 'column'
    }
});

export default NotificationsHome;
